import { useState, useCallback, useRef } from 'react';
import { debounce } from 'lodash';
import * as streetApiModule from '../services/streetApi';

// Price and size options
const priceOptions = [
  { value: '1000', label: '1 000 zł' },
  { value: '1500', label: '1 500 zł' },
  { value: '2000', label: '2 000 zł' },
  { value: '2500', label: '2 500 zł' },
  { value: '3000', label: '3 000 zł' },
  { value: '3500', label: '3 500 zł' },
  { value: '4000', label: '4 000 zł' },
  { value: '4500', label: '4 500 zł' },
  { value: '5000', label: '5 000 zł' },
  { value: '6000', label: '6 000 zł' },
  { value: '7000', label: '7 000 zł' },
  { value: '8000', label: '8 000 zł' },
  { value: '10000', label: '10 000 zł' }
];

const sizeOptions = [
  { value: '20', label: '20 m²' },
  { value: '30', label: '30 m²' },
  { value: '40', label: '40 m²' },
  { value: '50', label: '50 m²' },
  { value: '60', label: '60 m²' },
  { value: '70', label: '70 m²' },
  { value: '80', label: '80 m²' },
  { value: '90', label: '90 m²' },
  { value: '100', label: '100 m²' },
  { value: '120', label: '120 m²' },
  { value: '150', label: '150 m²' },
  { value: '200', label: '200 m²' }
];

// Available amenities
const availableAmenities = [
  'Balkon',
  'Akceptuje zwierzęta',
  'Garaż',
  'Parking',
  'Winda',
  'Piwnica',
  'Internet',
  'Zmywarka',
  'Ogródek',
  'Ostatnie piętro',
  'Parter'
];

export const useSearch = () => {
  // Autocomplete states
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [isLoadingCities, setIsLoadingCities] = useState(false);
  const [cityInputValue, setCityInputValue] = useState('');
  
  const [districtSuggestions, setDistrictSuggestions] = useState([]);
  const [isLoadingDistricts, setIsLoadingDistricts] = useState(false);
  const [districtInputValue, setDistrictInputValue] = useState('');
  const [_currentCity, _setCurrentCity] = useState('');
  
  const [streetSuggestions, setStreetSuggestions] = useState([]);
  const [isLoadingStreets, setIsLoadingStreets] = useState(false);
  const [streetInputValue, setStreetInputValue] = useState('');
  const latestCityQuery = useRef('');
  
  // Display states for force field updates
  const [displayCity, setDisplayCity] = useState('');
  const [displayDistrict, setDisplayDistrict] = useState('');
  
  // Popover anchor states
  const [anchorEls, setAnchorEls] = useState({
    city: null,
    rooms: null,
    price: null,
    size: null,
    amenities: null,
    maxPersons: null,
    dateAdded: null,
    smoking: null
  });

  // Autocomplete value helpers
  const autocompletePriceValue = useCallback((input) => {
    if (!input || input === '') return '';
    const num = input.replace(/[^\d]/g, '');
    if (num.length <= 1) return num;
    if (num.length === 2) return num + '00';
    if (num.length === 3) return num + '0';
    return num;
  }, []);

  const autocompleteSizeValue = useCallback((input) => {
    if (!input || input === '') return '';
    const num = input.replace(/[^\d]/g, '');
    return num;
  }, []);

  // City autocomplete functions
  // City autocomplete - tylko lokalna lista, zero API
  const fetchCitiesForMainFilters = null; // nieużywane

  // Street autocomplete functions
  const fetchStreetsForMainFilters = useCallback(
    debounce(async (cityName, query) => {
      if (!cityName || query.length < 2) {
        setStreetSuggestions([]);
        setIsLoadingStreets(false);
        return;
      }

      setIsLoadingStreets(true);
      try {
        const streetApi = await import('../services/streetApi');
        const streets = await streetApi.getStreets(cityName, query);
        setStreetSuggestions(streets || []);
      } catch (error) {
        setStreetSuggestions([]);
      } finally {
        setIsLoadingStreets(false);
      }
    }, 300),
    []
  );

  // District autocomplete function (Google Places)
  const fetchDistrictsForMainFilters = useCallback(
    debounce(async (cityName, query) => {
      if (!cityName) return;
      setIsLoadingDistricts(true);
      try {
        const streetApi = await import('../services/streetApi');
        const districts = await streetApi.fetchDistricts(cityName, query);
        setDistrictSuggestions(districts || []);
      } catch (error) {
        setDistrictSuggestions([]);
      } finally {
        setIsLoadingDistricts(false);
      }
    }, 300),
    []
  );

  // Input change handlers
  const handleCityInputChange = useCallback((event, newInputValue) => {
    setCityInputValue(newInputValue);
    latestCityQuery.current = newInputValue;
    // MUI filters options via filterOptions prop — no need to update state here
  }, []);

  const handleCityChangeForFilters = useCallback((event, newValue, updateSearchFilter) => {
    const cityName = newValue?.name || newValue || '';
    _setCurrentCity(cityName);
    setDistrictSuggestions([]);
    if (cityName) {
      setIsLoadingDistricts(true);
      import('../services/streetApi').then(streetApi => {
        streetApi.fetchDistricts(cityName, '').then(districts => {
          setDistrictSuggestions(districts || []);
        }).finally(() => setIsLoadingDistricts(false));
      });
    }
    updateSearchFilter('city', cityName);
  }, []);

  const handleDistrictInputChange = useCallback((event, newInputValue) => {
    setDistrictInputValue(newInputValue);
    if (_currentCity) {
      if (newInputValue.length >= 1) {
        setIsLoadingDistricts(true);
      }
      fetchDistrictsForMainFilters(_currentCity, newInputValue);
    }
  }, [_currentCity, fetchDistrictsForMainFilters]);

  const handleDistrictChange = useCallback((event, newValue, updateSearchFilter) => {
    const districtName = newValue?.name || newValue || '';
    updateSearchFilter('district', districtName);
    setDistrictInputValue(districtName);
  }, []);

  const handleStreetInputChange = useCallback((event, newInputValue, cityName) => {
    setStreetInputValue(newInputValue);
    if (cityName) {
      if (newInputValue.length >= 2) {
        setIsLoadingStreets(true); // Show loading immediately (before debounce) so dropdown stays open
      } else {
        setStreetSuggestions([]);
        setIsLoadingStreets(false);
      }
      fetchStreetsForMainFilters(cityName, newInputValue);
    } else {
      setStreetSuggestions([]);
    }
  }, [fetchStreetsForMainFilters]);

  const handleStreetChange = useCallback((event, newValue, updateSearchFilter) => {
    const streetName = newValue?.name || newValue || '';
    updateSearchFilter('street', streetName);
    setStreetInputValue(streetName);
  }, []);

  // Popover handlers
  const handlePopoverOpen = useCallback((filterType) => (event) => {
    setAnchorEls(prev => ({
      ...prev,
      [filterType]: event.currentTarget
    }));
    
    // Show popular cities when opening city popover
    if (filterType === 'city') {
      const popularCities = streetApiModule.getPopularCities();
      setCitySuggestions(popularCities);
    }
  }, []);

  const handlePopoverClose = useCallback((filterType) => () => {
    setAnchorEls(prev => ({
      ...prev,
      [filterType]: null
    }));
  }, []);

  // Search execution
  const executeSearch = useCallback(async (searchFilters, setSearchResults, setDataLoading) => {
    setDataLoading(true);
    try {
      const supabaseService = await import('../services/supabaseService');
      const results = await supabaseService.propertiesService.search(searchFilters);
      setSearchResults(results || []);
    } catch (error) {
      setSearchResults([]);
    } finally {
      setDataLoading(false);
    }
  }, []);

  return {
    // State
    citySuggestions,
    isLoadingCities,
    cityInputValue,
    districtSuggestions,
    isLoadingDistricts,
    districtInputValue,
    streetSuggestions,
    isLoadingStreets,
    streetInputValue,
    displayCity,
    displayDistrict,
    anchorEls,
    
    // Constants
    priceOptions,
    sizeOptions,
    availableAmenities,
    
    // Setters
    setCityInputValue,
    setDistrictInputValue,
    setStreetInputValue,
    setDisplayCity,
    setDisplayDistrict,
    setDistrictSuggestions,
    setStreetSuggestions,
    
    // Handlers
    handleCityInputChange,
    handleCityChangeForFilters,
    handleDistrictInputChange,
    handleDistrictChange,
    handleStreetInputChange,
    handleStreetChange,
    handlePopoverOpen,
    handlePopoverClose,
    
    // Utilities
    autocompletePriceValue,
    autocompleteSizeValue,
    executeSearch
  };
};